interface BrianDatadogConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  /** Must match the backend's `DD_ENV` or RUM sessions won't line up with APM traces. */
  env: string;
  enabled: boolean;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  defaultPrivacyLevel: "mask" | "mask-user-input" | "allow";
}

interface BrianConfig {
  apiUrl: string;
  wsUrl: string;
  datadog: BrianDatadogConfig;
}

/** Every default is the quiet choice, so a missing or partial `datadog` block (a cached pre-RUM
 * `env-config.js`, localhost) inherits silence rather than capture. */
const datadogDefaults: BrianDatadogConfig = {
  applicationId: "",
  clientToken: "",
  site: "datadoghq.com",
  // Distinct from the backend's `brian-web` service.
  service: "brian-frontend",
  env: "local",
  enabled: false,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  defaultPrivacyLevel: "mask",
};

/** `env-config.js` is hand-written JS, so every field (including `datadog` keys) is optional. */
type ExternalBrianConfig = Partial<Omit<BrianConfig, "datadog">> & {
  datadog?: Partial<BrianDatadogConfig>;
};

declare global {
  interface Window {
    __SCRIBENOTE_BRIAN_CONFIG__?: ExternalBrianConfig;
  }
}

export function getConfig(): BrianConfig {
  const config = window.__SCRIBENOTE_BRIAN_CONFIG__;
  if (!config?.apiUrl || !config.wsUrl) {
    throw new Error(
      "Brian config not loaded. Ensure env-config.js is included before the app script.",
    );
  }
  return {
    apiUrl: config.apiUrl,
    wsUrl: config.wsUrl,
    datadog: { ...datadogDefaults, ...config.datadog },
  };
}
